export interface RecordingResult {
  blob: Blob;
  mimeType: string;
  durationSeconds: number;
}

const mimeCandidates = [
  'video/mp4;codecs=h264,aac',
  'video/webm;codecs=vp8,opus',
  'video/webm;codecs=vp9,opus',
  'video/webm',
];

export function selectRecordingMime(isSupported: (mime: string) => boolean = MediaRecorder.isTypeSupported): string {
  // 编码能力由浏览器决定，不能把 H.265 或某一种容器硬编码成所有设备的前提。
  return mimeCandidates.find((mime) => isSupported(mime)) ?? '';
}

export class CommitmentRecorder {
  private recorder: MediaRecorder | null = null;
  private chunks: BlobPart[] = [];
  private startedAt = 0;

  start(stream: MediaStream): string {
    if (typeof MediaRecorder === 'undefined') {
      throw new Error('当前浏览器不支持视频录制');
    }
    if (stream.getAudioTracks().length === 0) {
      throw new Error('未取得麦克风权限');
    }
    const mimeType = selectRecordingMime();
    this.chunks = [];
    // 约 1.2 Mbps 可把 20 秒视频控制在较小体积，同时保留足够的人脸和口型细节。
    this.recorder = mimeType ? new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 1_200_000 }) : new MediaRecorder(stream);
    const recorder = this.recorder;
    this.recorder.addEventListener('dataavailable', (event) => {
      // 重置后旧录制器仍可能回传最后一片数据，不能混入新一轮视频。
      if (this.recorder === recorder && event.data.size > 0) this.chunks.push(event.data);
    });
    this.startedAt = performance.now();
    this.recorder.start(1000);
    return this.recorder.mimeType || mimeType || 'video/webm';
  }

  async stop(): Promise<RecordingResult> {
    if (!this.recorder || this.recorder.state === 'inactive') {
      throw new Error('当前没有正在进行的录制');
    }
    const recorder = this.recorder;
    const chunks = this.chunks;
    const durationSeconds = (performance.now() - this.startedAt) / 1000;
    return await new Promise<RecordingResult>((resolve, reject) => {
      recorder.addEventListener(
        'stop',
        () => {
          if (this.recorder !== recorder) {
            reject(new Error('本次录制已取消'));
            return;
          }
          const blob = new Blob(chunks, { type: recorder.mimeType || 'video/webm' });
          this.recorder = null;
          if (!blob.size) {
            reject(new Error('录制结果为空'));
            return;
          }
          resolve({ blob, mimeType: blob.type, durationSeconds });
        },
        { once: true },
      );
      recorder.addEventListener('error', () => reject(new Error('视频录制失败')), { once: true });
      recorder.stop();
    });
  }

  cancel(): void {
    if (this.recorder && this.recorder.state !== 'inactive') this.recorder.stop();
    this.recorder = null;
    this.chunks = [];
  }
}
